import "./formulaireAbsence.css";
import React, { useState, useRef, useCallback, useEffect } from "react";
import FormConfirmation from "./components/FormConfirmation";
import Select from "react-select";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import schools from "./schools.json";
import disciplines from "./disciplines.json";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import EmailVerification from "./components/EmailVerification";

const MAX_PREFERENCES = 4;

function DisciplinePreference() {
  useEffect(() => { document.title = "SEVF | Préférence de disciplines"; }, []);

  const [submittedData, setSubmittedData] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const [form_Data, set_Form_Data] = useState({
    firstName: "",
    lastName: "",
    establishment: "",
    matricule: "",
    phoneNumber: "",
    currentDiscipline: "",
    preference1: "",
    preference2: "",
    preference3: "",
    preference4: "",
    brevetPieceJointe: false,
    brevetDossier: false,
    experiencePieceJointe: false,
    experienceDossier: false,
    etudesPieceJointe: false,
    etudesDossier: false,
    files: [],
    emailVerified: false,
    emailToVerify: "",
  });

  const initialFormData = {
    firstName: "",
    lastName: "",
    establishment: "",
    matricule: "",
    phoneNumber: "",
    currentDiscipline: "",
    preference1: "",
    preference2: "",
    preference3: "",
    preference4: "",
    brevetPieceJointe: false,
    brevetDossier: false,
    experiencePieceJointe: false,
    experienceDossier: false,
    etudesPieceJointe: false,
    etudesDossier: false,
    files: [],
    emailVerified: false,
    emailToVerify: "",
  };

  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formValidated, setFormValidated] = useState(false);
  const [firstFormValidation, setFirstFormValidation] = useState(false);
  const [acknowledgeSignature, setAcknowledgeSignature] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [preferenceCount, setPreferenceCount] = useState(1);

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const establishmentRef = useRef(null);
  const matriculeRef = useRef(null);
  const phoneRef = useRef(null);
  const currentDisciplineRef = useRef(null);
  const preferencesRef = useRef(null);
  const criteresRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleChange = (event) => {
    const { name, value } = event.target;
    set_Form_Data((prevData) => {
      const updatedData = { ...prevData, [name]: value };
      if (firstFormValidation) {
        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
      }
      return updatedData;
    });
  };

  // Get filtered options for a given preference slot
  const getFilteredOptions = (prefIndex) => {
    const selected = [form_Data.currentDiscipline];
    for (let i = 1; i <= MAX_PREFERENCES; i++) {
      if (i !== prefIndex && form_Data[`preference${i}`]) {
        selected.push(form_Data[`preference${i}`]);
      }
    }
    return disciplines.disciplines
      .filter((d) => !selected.includes(d))
      .map((d) => ({ value: d, label: d }));
  };

  const validateFormData = (form_Data, acknowledgeSignature) => {
    const errors = {};

    if (!form_Data.firstName) {
      errors.firstName = "Le prénom est requis.";
    } else if (/\d/.test(form_Data.firstName)) {
      errors.firstName = "Le prénom ne doit pas contenir de chiffres.";
    }

    if (!form_Data.lastName) {
      errors.lastName = "Le nom est requis.";
    } else if (/\d/.test(form_Data.lastName)) {
      errors.lastName = "Le nom ne doit pas contenir de chiffres.";
    }

    if (!form_Data.establishment) {
      errors.establishment = "Le nom de l'école est requis.";
    }

    if (!form_Data.matricule) {
      errors.matricule = "Le numéro de matricule est requis.";
    } else if (form_Data.matricule.length > 7) {
      errors.matricule =
        "Le numéro de matricule ne doit pas dépasser 7 chiffres.";
    }

    if (!form_Data.phoneNumber) {
      errors.phoneNumber = "Le numéro de téléphone est requis.";
    } else if (form_Data.phoneNumber.length !== 12) {
      errors.phoneNumber = "Le numéro de téléphone doit contenir 10 chiffres.";
    }

    if (!form_Data.currentDiscipline) {
      errors.currentDiscipline =
        "Veuillez sélectionner votre discipline actuelle.";
    }

    if (form_Data.currentDiscipline && !form_Data.preference1) {
      errors.preference1 =
        "Veuillez sélectionner au moins une préférence de discipline.";
    }

    const hasPreference = form_Data.preference1;

    const hasCritere =
      form_Data.brevetPieceJointe ||
      form_Data.brevetDossier ||
      form_Data.experiencePieceJointe ||
      form_Data.experienceDossier ||
      form_Data.etudesPieceJointe ||
      form_Data.etudesDossier;

    if (hasPreference && !hasCritere) {
      errors.criteres = "Veuillez cocher au moins un critère de capacité.";
    }

    if (hasPreference && hasCritere && !acknowledgeSignature) {
      errors.acknowledgeSignature =
        "Veuillez cocher la case pour accepter les avertissements.";
    }

    if (hasPreference && hasCritere && !emailVerified) {
      errors.emailVerified = "Veuillez vérifier votre adresse e-mail.";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateFormData(form_Data, acknowledgeSignature);

    if (!firstFormValidation) {
      setFirstFormValidation(true);
    }

    if (Object.keys(errors).length === 0) {
      setFormValidated(true);
      setFormErrors({});

      try {
        setSubmitError(null);
        setLoading(true);

        const formDataToSend = new FormData();
        formDataToSend.append("firstName", form_Data.firstName);
        formDataToSend.append("lastName", form_Data.lastName);
        formDataToSend.append("establishment", form_Data.establishment);
        formDataToSend.append("matricule", form_Data.matricule);
        formDataToSend.append("phoneNumber", form_Data.phoneNumber);
        formDataToSend.append("currentDiscipline", form_Data.currentDiscipline);
        formDataToSend.append("preference1", form_Data.preference1);
        if (form_Data.preference2)
          formDataToSend.append("preference2", form_Data.preference2);
        if (form_Data.preference3)
          formDataToSend.append("preference3", form_Data.preference3);
        if (form_Data.preference4)
          formDataToSend.append("preference4", form_Data.preference4);
        formDataToSend.append("brevetPieceJointe", form_Data.brevetPieceJointe);
        formDataToSend.append("brevetDossier", form_Data.brevetDossier);
        formDataToSend.append(
          "experiencePieceJointe",
          form_Data.experiencePieceJointe,
        );
        formDataToSend.append("experienceDossier", form_Data.experienceDossier);
        formDataToSend.append("etudesPieceJointe", form_Data.etudesPieceJointe);
        formDataToSend.append("etudesDossier", form_Data.etudesDossier);
        formDataToSend.append("emailToVerify", form_Data.emailToVerify);

        // Append user-uploaded files
        if (form_Data.files && form_Data.files.length > 0) {
          for (const file of form_Data.files) {
            formDataToSend.append("files", file);
          }
        }

        const response = await fetch(
          `${apiUrl}/document/discipline-preference`,
          {
            method: "POST",
            body: formDataToSend,
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.errors?.[0] || "Erreur lors de l'envoi du formulaire.",
          );
        }

        // Capture data for confirmation screen before resetting
        const prefs = [form_Data.preference1, form_Data.preference2, form_Data.preference3, form_Data.preference4]
          .filter(Boolean)
          .join(", ");
        const captured = {
          name: `${form_Data.firstName} ${form_Data.lastName}`,
          matricule: form_Data.matricule,
          establishment: form_Data.establishment,
          disciplineActuelle: form_Data.currentDiscipline || "—",
          preferences: prefs || "—",
          dateSent: new Date().toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" }),
        };

        // Reset the form state
        set_Form_Data(initialFormData);
        setAcknowledgeSignature(false);
        setEmailVerified(false);
        setFirstFormValidation(false);
        setFormValidated(false);
        setFormErrors({});
        setPreferenceCount(1);
        if (fileInputRef.current) fileInputRef.current.value = "";

        setSubmittedData(captured);
      } catch (error) {
        console.error("Error:", error);
        setSubmitError(
          error.message && error.message !== "Failed to fetch"
            ? error.message
            : "Une erreur s'est produite. Veuillez réessayer. Si le problème persiste, veuillez contacter valeriec@sevf.ca.",
        );
      } finally {
        setLoading(false);
      }
    } else {
      setFormErrors(errors);
      setFormValidated(true);

      if (errors.firstName) {
        firstNameRef.current.scrollIntoView({ behavior: "smooth" });
      } else if (errors.lastName) {
        lastNameRef.current.scrollIntoView({ behavior: "smooth" });
      } else if (errors.establishment) {
        establishmentRef.current.scrollIntoView({ behavior: "smooth" });
      } else if (errors.matricule) {
        matriculeRef.current.scrollIntoView({ behavior: "smooth" });
      } else if (errors.phoneNumber) {
        phoneRef.current.scrollIntoView({ behavior: "smooth" });
      } else if (errors.currentDiscipline) {
        currentDisciplineRef.current.scrollIntoView({ behavior: "smooth" });
      } else if (errors.preference1 && preferencesRef.current) {
        preferencesRef.current.scrollIntoView({ behavior: "smooth" });
      } else if (errors.criteres && criteresRef.current) {
        criteresRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handlePhoneChange = (value) => {
    set_Form_Data((prevData) => {
      const updatedData = { ...prevData, phoneNumber: value };
      if (firstFormValidation) {
        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
      }
      return updatedData;
    });
  };

  const ALLOWED_EXTENSIONS = [".docx", ".pdf", ".doc", ".jpg", ".png", ".jpeg"];
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
  const MAX_FILES = 3;

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const currentFiles = form_Data.files || [];

    if (currentFiles.length + selectedFiles.length > MAX_FILES) {
      setFormErrors((prev) => ({
        ...prev,
        files: `Vous pouvez téléverser un maximum de ${MAX_FILES} fichiers.`,
      }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const validFiles = [];
    for (const file of selectedFiles) {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setFormErrors((prev) => ({
          ...prev,
          files: `Le fichier "${file.name}" n'est pas un format accepté. Formats acceptés : DOCX, PDF, DOC, JPG, PNG, JPEG.`,
        }));
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setFormErrors((prev) => ({
          ...prev,
          files: `Le fichier "${file.name}" dépasse la taille maximale de 20 MB.`,
        }));
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      validFiles.push(file);
    }

    set_Form_Data((prevData) => {
      const updatedData = {
        ...prevData,
        files: [...currentFiles, ...validFiles],
      };
      if (firstFormValidation) {
        const updatedErrors = validateFormData(
          updatedData,
          acknowledgeSignature,
        );
        delete updatedErrors.files;
        setFormErrors(updatedErrors);
      }
      return updatedData;
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveFile = (index) => {
    set_Form_Data((prevData) => {
      const updatedFiles = prevData.files.filter((_, i) => i !== index);
      const updatedData = { ...prevData, files: updatedFiles };
      if (firstFormValidation) {
        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
      }
      return updatedData;
    });
  };

  const handleCritereChange = (e) => {
    const { name, checked } = e.target;
    set_Form_Data((prevData) => {
      const updatedData = { ...prevData, [name]: checked };
      // Clear files if no pièce jointe checkboxes remain checked
      const hasPieceJointe =
        updatedData.brevetPieceJointe ||
        updatedData.experiencePieceJointe ||
        updatedData.etudesPieceJointe;
      if (!hasPieceJointe) {
        updatedData.files = [];
      }
      if (firstFormValidation) {
        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
      }
      return updatedData;
    });
  };

  const handleAcknowledgeSignatureChange = (e) => {
    const newVal = e.target.checked;
    setAcknowledgeSignature(newVal);
    if (firstFormValidation) {
      const updatedErrors = validateFormData(form_Data, newVal);
      setFormErrors(updatedErrors);
    }
  };

  const handleEmailVerified = useCallback((email) => {
    setEmailVerified(true);
    set_Form_Data((prev) => ({
      ...prev,
      emailVerified: true,
      emailToVerify: email,
    }));
    setFormErrors((prev) => ({
      ...prev,
      emailVerified: undefined,
    }));
  }, []);

  const handlePreferenceChange = (prefIndex, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    set_Form_Data((prevData) => {
      const updatedData = { ...prevData, [`preference${prefIndex}`]: value };
      // Reset later preferences when an earlier one changes
      for (let i = prefIndex + 1; i <= MAX_PREFERENCES; i++) {
        updatedData[`preference${i}`] = "";
      }
      if (firstFormValidation) {
        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
      }
      return updatedData;
    });
    // Also reduce preferenceCount if we cleared later ones
    if (!value) {
      setPreferenceCount(Math.max(1, prefIndex));
    } else {
      setPreferenceCount((prev) => Math.max(prev, prefIndex));
    }
  };

  const handleAddPreference = () => {
    setPreferenceCount((prev) => Math.min(prev + 1, MAX_PREFERENCES));
  };

  const handleRemovePreference = (prefIndex) => {
    set_Form_Data((prevData) => {
      const updatedData = { ...prevData };
      // Clear this preference and all later ones
      for (let i = prefIndex; i <= MAX_PREFERENCES; i++) {
        updatedData[`preference${i}`] = "";
      }
      if (firstFormValidation) {
        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
      }
      return updatedData;
    });
    setPreferenceCount(prefIndex - 1);
  };

  const modernSelectStyles = (hasError, isValid) => ({
    control: (base, state) => ({
      ...base,
      borderColor: hasError ? "#ba1a1a" : isValid ? "#198754" : "transparent",
      backgroundColor: "#ffffff",
      borderRadius: "0.5rem",
      boxShadow: state.isFocused ? "0 0 0 2px #00447f" : "0 1px 3px rgba(0,0,0,0.08)",
      minHeight: "48px",
      "&:hover": { borderColor: "#00447f" },
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  });

  const inputStyle = (hasError, isValid) => ({
    width: "100%",
    backgroundColor: "#ffffff",
    border: hasError ? "1.5px solid #ba1a1a" : isValid ? "1.5px solid #198754" : "none",
    borderRadius: "0.5rem",
    padding: "0.75rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    outline: "none",
    fontSize: "0.95rem",
    boxSizing: "border-box",
    fontFamily: "Inter, sans-serif",
  });

  const labelStyle = {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#414751",
    display: "block",
    marginBottom: "0.5rem",
  };

  const errorStyle = {
    color: "#ba1a1a",
    fontSize: "0.8rem",
    marginTop: "0.25rem",
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "2rem",
  };

  const sectionBadgeStyle = {
    backgroundColor: "#00447f",
    color: "#ffffff",
    width: 32,
    height: 32,
    borderRadius: "9999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    flexShrink: 0,
    fontSize: "0.95rem",
  };

  const sectionTitleStyle = {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#00447f",
    margin: 0,
    fontFamily: "Public Sans, sans-serif",
  };

  const checkboxStyle = {
    accentColor: "#00447f",
    width: 18,
    height: 18,
    flexShrink: 0,
    cursor: "pointer",
  };

  const preferenceLabels = ["1er choix", "2e choix", "3e choix", "4e choix"];

  if (submittedData) {
    return (
      <FormConfirmation
        headerTitle="Préférence pour une ou des disciplines"
        headerSub="Soumission des préférences de disciplines pour la liste de priorité."
        dateSent={submittedData.dateSent}
        details={[
          { label: "Employé(e)", value: submittedData.name },
          { label: "Matricule", value: submittedData.matricule },
          { label: "Établissement", value: submittedData.establishment },
          { label: "Discipline actuelle", value: submittedData.disciplineActuelle },
          { label: "Préférences soumises", value: submittedData.preferences },
        ]}
      />
    );
  }

  return (
    <div style={{ backgroundColor: "#f9f9ff", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #00447f 0%, #0066cc 100%)", padding: "1.25rem 1.5rem", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap", boxShadow: "0 2px 12px rgba(0,68,127,0.18)" }}>
        <Link to="/">
          <img src="logo.jpg" alt="logo" style={{ maxHeight: "56px", width: "auto", borderRadius: "0.5rem", backgroundColor: "rgba(255,255,255,0.95)", padding: "0.25rem", flexShrink: 0 }} />
        </Link>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ color: "#ffffff", fontSize: "1.2rem", fontWeight: 700, margin: 0, lineHeight: 1.2 }}>Préférence pour une ou des disciplines</h1>
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.85rem", margin: "0.25rem 0 0 0" }}>Soumission des préférences de disciplines pour la liste de priorité.</p>
        </div>
      </div>

      {/* Page content */}
      <div style={{ maxWidth: "64rem", margin: "0 auto", padding: "2rem 1.5rem 8rem" }}>

        {/* Notes banner */}
        <div
          style={{
            backgroundColor: "rgba(212, 227, 255, 0.35)",
            borderRadius: "0.75rem",
            padding: "1.5rem",
            marginBottom: "3rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
            <span className="material-symbols-outlined" style={{ color: "#005ca8", display: "inline-block", lineHeight: 1, flexShrink: 0 }}>
              info
            </span>
            <h4 style={{ fontWeight: 700, color: "#00447f", margin: 0, fontFamily: "Public Sans, sans-serif" }}>
              Remarques
            </h4>
          </div>
          <ul style={{ paddingLeft: "1.75rem", margin: 0, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <li style={{ color: "#414751", fontSize: "0.9rem" }}>
              Veuillez respecter la date limite pour transmettre votre demande.
            </li>
            <li style={{ color: "#414751", fontSize: "0.9rem" }}>
              Si vous avez des questions veuillez communiquer avec Dany Blackburn (
              <a href="mailto:danyb@sevf.ca" style={{ color: "#00447f" }}>danyb@sevf.ca</a>)
            </li>
            <li style={{ color: "#414751", fontSize: "0.9rem" }}>
              Suite à la transmission de votre demande, le Centre de service en fera l'analyse et vous rendra une décision.
            </li>
          </ul>
        </div>

        <form
          noValidate
          onSubmit={handleSubmit}
          id="form-container"
          style={{ display: "flex", flexDirection: "column", gap: "2.5rem" }}
        >
          {/* ── Section 1 : Informations Personnelles ── */}
          <section style={{ backgroundColor: "#f2f3fe", borderRadius: "0.75rem", padding: "2rem" }}>
            <div style={sectionHeaderStyle}>
              <span style={sectionBadgeStyle}>1</span>
              <h2 style={sectionTitleStyle}>Informations Personnelles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: "1.5rem" }}>
              <div ref={firstNameRef}>
                <label htmlFor="firstName" style={labelStyle}>Prénom</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={form_Data.firstName}
                  onChange={handleChange}
                  required
                  placeholder="Entrez votre prénom"
                  style={inputStyle(formErrors.firstName, formValidated && !formErrors.firstName)}
                />
                {formErrors.firstName && <p style={errorStyle}>{formErrors.firstName}</p>}
              </div>

              <div ref={lastNameRef}>
                <label htmlFor="lastName" style={labelStyle}>Nom de famille</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={form_Data.lastName}
                  onChange={handleChange}
                  required
                  placeholder="Entrez votre nom"
                  style={inputStyle(formErrors.lastName, formValidated && !formErrors.lastName)}
                />
                {formErrors.lastName && <p style={errorStyle}>{formErrors.lastName}</p>}
              </div>

              <div ref={establishmentRef} className="md:col-span-2">
                <label htmlFor="establishment" style={labelStyle}>Nom de l'école</label>
                <Select
                  id="establishment"
                  name="establishment"
                  options={schools
                    .sort((a, b) => a.number - b.number)
                    .map((school) => ({
                      value: `${school.number} ${school.name}`,
                      label: `${school.number} ${school.name}`,
                    }))}
                  value={
                    form_Data.establishment
                      ? { value: form_Data.establishment, label: form_Data.establishment }
                      : null
                  }
                  onChange={(selectedOption) => {
                    const value = selectedOption ? selectedOption.value : "";
                    set_Form_Data((prevData) => {
                      const updatedData = { ...prevData, establishment: value };
                      if (firstFormValidation) {
                        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
                      }
                      return updatedData;
                    });
                  }}
                  placeholder="Rechercher une école..."
                  classNamePrefix="react-select"
                  styles={modernSelectStyles(formErrors.establishment, formValidated && !formErrors.establishment)}
                />
                {formErrors.establishment && <p style={errorStyle}>{formErrors.establishment}</p>}
              </div>

              <div ref={matriculeRef}>
                <label htmlFor="matricule" style={labelStyle}>Numéro de matricule</label>
                <input
                  type="number"
                  id="matricule"
                  name="matricule"
                  value={form_Data.matricule}
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()}
                  required
                  placeholder="123456"
                  style={inputStyle(formErrors.matricule, formValidated && !formErrors.matricule)}
                />
                {formErrors.matricule && <p style={errorStyle}>{formErrors.matricule}</p>}
              </div>

              <div ref={phoneRef}>
                <label
                  style={{
                    ...labelStyle,
                    color: formErrors.phoneNumber ? "#ba1a1a" : formValidated && !formErrors.phoneNumber ? "#198754" : "#414751",
                  }}
                >
                  Numéro de téléphone
                </label>
                <div
                  style={{
                    border: formErrors.phoneNumber
                      ? "1.5px solid #ba1a1a"
                      : formValidated && !formErrors.phoneNumber
                        ? "1.5px solid #198754"
                        : "none",
                    backgroundColor: "#ffffff",
                    borderRadius: "0.5rem",
                    padding: "0.5rem 0.75rem",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  <PhoneInput
                    defaultCountry="CA"
                    placeholder="XXX-XXX-XXXX"
                    value={form_Data.phoneNumber}
                    onChange={handlePhoneChange}
                    name="phoneNumber"
                    required
                  />
                </div>
                {formErrors.phoneNumber && <p style={errorStyle}>{formErrors.phoneNumber}</p>}
              </div>
            </div>
          </section>

          {/* ── Section 2 : Discipline actuelle ── */}
          <section
            ref={currentDisciplineRef}
            style={{
              backgroundColor: "#f2f3fe",
              borderRadius: "0.75rem",
              padding: "2rem",
              border: formErrors.currentDiscipline ? "1.5px solid #ba1a1a" : "none",
            }}
          >
            <div style={sectionHeaderStyle}>
              <span style={sectionBadgeStyle}>2</span>
              <h2 style={sectionTitleStyle}>Discipline actuelle</h2>
            </div>
            <label htmlFor="currentDiscipline" style={labelStyle}>
              Discipline dans laquelle <strong>JE SUIS PRÉSENTEMENT</strong> inscrit(e)
            </label>
            <Select
              id="currentDiscipline"
              name="currentDiscipline"
              options={disciplines.disciplines.map((discipline) => ({
                value: discipline,
                label: discipline,
              }))}
              value={
                form_Data.currentDiscipline
                  ? { value: form_Data.currentDiscipline, label: form_Data.currentDiscipline }
                  : null
              }
              onChange={(selectedOption) => {
                const value = selectedOption ? selectedOption.value : "";
                set_Form_Data((prevData) => {
                  const updatedData = {
                    ...prevData,
                    currentDiscipline: value,
                    preference1: "",
                    preference2: "",
                    preference3: "",
                    preference4: "",
                  };
                  if (firstFormValidation) {
                    setFormErrors(validateFormData(updatedData, acknowledgeSignature));
                  }
                  return updatedData;
                });
                setPreferenceCount(1);
              }}
              placeholder="Rechercher une discipline..."
              classNamePrefix="react-select"
              styles={modernSelectStyles(formErrors.currentDiscipline, formValidated && !formErrors.currentDiscipline)}
            />
            {formErrors.currentDiscipline && <p style={errorStyle}>{formErrors.currentDiscipline}</p>}
          </section>

          {/* ── Section 3 : Préférences ── */}
          {form_Data.currentDiscipline && (
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              ref={preferencesRef}
              style={{
                backgroundColor: "#f2f3fe",
                borderRadius: "0.75rem",
                padding: "2rem",
                border: formErrors.preference1 ? "1.5px solid #ba1a1a" : "none",
              }}
            >
              <div style={sectionHeaderStyle}>
                <span style={sectionBadgeStyle}>3</span>
                <h2 style={sectionTitleStyle}>Préférences de discipline</h2>
              </div>
              <p style={{ fontSize: "0.875rem", color: "#414751", marginBottom: "1.25rem" }}>
                Discipline(s) dans laquelle (lesquelles) <strong>JE DÉSIRE</strong> signifier une préférence pour la prochaine année scolaire :
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {Array.from({ length: preferenceCount }, (_, i) => i + 1).map((prefIndex) => (
                  <div key={prefIndex}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
                      <label htmlFor={`preference${prefIndex}`} style={{ ...labelStyle, marginBottom: 0 }}>
                        {preferenceLabels[prefIndex - 1]}
                      </label>
                      {prefIndex > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemovePreference(prefIndex)}
                          style={{
                            marginLeft: "auto",
                            backgroundColor: "transparent",
                            border: "1.5px solid #ba1a1a",
                            color: "#ba1a1a",
                            borderRadius: "0.375rem",
                            padding: "0.2rem 0.6rem",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Retirer
                        </button>
                      )}
                    </div>
                    <Select
                      id={`preference${prefIndex}`}
                      name={`preference${prefIndex}`}
                      options={getFilteredOptions(prefIndex)}
                      value={
                        form_Data[`preference${prefIndex}`]
                          ? { value: form_Data[`preference${prefIndex}`], label: form_Data[`preference${prefIndex}`] }
                          : null
                      }
                      onChange={(selectedOption) => handlePreferenceChange(prefIndex, selectedOption)}
                      placeholder="Rechercher une discipline..."
                      classNamePrefix="react-select"
                      styles={modernSelectStyles(
                        prefIndex === 1 ? formErrors.preference1 : null,
                        prefIndex === 1 ? formValidated && !formErrors.preference1 : false,
                      )}
                    />
                    {prefIndex === 1 && formErrors.preference1 && <p style={errorStyle}>{formErrors.preference1}</p>}
                  </div>
                ))}
              </div>

              {preferenceCount < MAX_PREFERENCES && form_Data[`preference${preferenceCount}`] && (
                <button
                  type="button"
                  onClick={handleAddPreference}
                  style={{
                    marginTop: "1rem",
                    backgroundColor: "transparent",
                    border: "1.5px solid #00447f",
                    color: "#00447f",
                    borderRadius: "0.5rem",
                    padding: "0.5rem 1.25rem",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  + Ajouter une préférence
                </button>
              )}
            </motion.section>
          )}

          {/* ── Section 4 : Critères ── */}
          {form_Data.preference1 && (
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              ref={criteresRef}
              style={{
                backgroundColor: "#f2f3fe",
                borderRadius: "0.75rem",
                padding: "2rem",
                border: formErrors.criteres ? "1.5px solid #ba1a1a" : "none",
              }}
            >
              <div style={sectionHeaderStyle}>
                <span style={sectionBadgeStyle}>4</span>
                <h2 style={sectionTitleStyle}>Critères de capacité</h2>
              </div>
              <p style={{ fontSize: "0.875rem", color: "#414751", marginBottom: "1.25rem" }}>
                Je réponds à l'un ou l'autre des critères de capacité définis à la clause 5-3.13 (cochez le ou les éléments approprié(s)) :
              </p>

              <div className="discipline-table-wrapper">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 130px 130px", gap: "0.5rem", marginBottom: "0.5rem", paddingBottom: "0.5rem", borderBottom: "1.5px solid #e2e4ea", minWidth: "400px" }}>
                <div />
                <div style={{ textAlign: "center", fontSize: "0.8rem", fontWeight: 700, color: "#414751" }}>Pièce jointe</div>
                <div style={{ textAlign: "center", fontSize: "0.8rem", fontWeight: 700, color: "#414751" }}>Pièce déjà à mon dossier CSS</div>
              </div>

              {[
                {
                  label: "A. Brevet ou certificat spécialisé pour la discipline visée.",
                  pieceId: "brevetPieceJointe",
                  dossierId: "brevetDossier",
                  pieceChecked: form_Data.brevetPieceJointe,
                  dossierChecked: form_Data.brevetDossier,
                },
                {
                  label: "B. Expérience — l'équivalent d'un an à temps complet dans la discipline visée à l'intérieur des 5 dernières années.",
                  pieceId: "experiencePieceJointe",
                  dossierId: "experienceDossier",
                  pieceChecked: form_Data.experiencePieceJointe,
                  dossierChecked: form_Data.experienceDossier,
                },
                {
                  label: "C. Études — minimum de 15 crédits dans la discipline visée dans le cadre d'un même programme d'études.",
                  pieceId: "etudesPieceJointe",
                  dossierId: "etudesDossier",
                  pieceChecked: form_Data.etudesPieceJointe,
                  dossierChecked: form_Data.etudesDossier,
                },
              ].map((row, i) => (
                <div
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 130px 130px",
                    gap: "0.5rem",
                    alignItems: "center",
                    backgroundColor: "#ffffff",
                    borderRadius: "0.5rem",
                    padding: "0.875rem 1rem",
                    marginBottom: "0.5rem",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    minWidth: "400px",
                  }}
                >
                  <span style={{ fontSize: "0.9rem", color: "#181c23" }}>{row.label}</span>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <input type="checkbox" id={row.pieceId} name={row.pieceId} checked={row.pieceChecked} onChange={handleCritereChange} style={checkboxStyle} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <input type="checkbox" id={row.dossierId} name={row.dossierId} checked={row.dossierChecked} onChange={handleCritereChange} style={checkboxStyle} />
                  </div>
                </div>
              ))}
              </div>

              {formErrors.criteres && <p style={{ ...errorStyle, marginTop: "0.75rem" }}>{formErrors.criteres}</p>}
            </motion.section>
          )}

          {/* ── Section 5 : Pièces jointes ── */}
          {(form_Data.brevetPieceJointe || form_Data.experiencePieceJointe || form_Data.etudesPieceJointe) && (
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ backgroundColor: "#f2f3fe", borderRadius: "0.75rem", padding: "2rem" }}
            >
              <div style={sectionHeaderStyle}>
                <span style={sectionBadgeStyle}>5</span>
                <h2 style={sectionTitleStyle}>Pièces jointes</h2>
              </div>
              <p style={{ fontSize: "0.875rem", color: "#414751", marginBottom: "0.5rem" }}>
                Formats acceptés : DOCX, PDF, DOC, JPG, PNG, JPEG — maximum 3 fichiers, 20 MB chacun
              </p>
              <div
                style={{
                  border: "2px dashed #c1c6d3",
                  borderRadius: "0.75rem",
                  padding: "1.5rem",
                  textAlign: "center",
                  backgroundColor: "#ffffff",
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: "2rem", color: "#727782", display: "block", marginBottom: "0.5rem" }}>cloud_upload</span>
                <input
                  type="file"
                  id="fileInput"
                  style={{ display: "none" }}
                  accept=".docx,.pdf,.doc,.jpg,.png,.jpeg"
                  multiple
                  onChange={handleFileInputChange}
                  ref={fileInputRef}
                  disabled={form_Data.files.length >= MAX_FILES}
                />
                <label
                  htmlFor="fileInput"
                  style={{
                    display: "inline-block",
                    backgroundColor: form_Data.files.length >= MAX_FILES ? "#e0e2ec" : "#00447f",
                    color: form_Data.files.length >= MAX_FILES ? "#727782" : "#ffffff",
                    border: "none",
                    borderRadius: "0.5rem",
                    padding: "0.625rem 1.5rem",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    cursor: form_Data.files.length >= MAX_FILES ? "not-allowed" : "pointer",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Choisir un fichier
                </label>
              </div>
              {form_Data.files.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "1rem" }}>
                  {form_Data.files.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "#ffffff",
                        borderRadius: "0.5rem",
                        padding: "0.75rem 1rem",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                      }}
                    >
                      <span style={{ fontSize: "0.875rem", color: "#181c23" }}>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        style={{
                          backgroundColor: "transparent",
                          border: "1.5px solid #ba1a1a",
                          color: "#ba1a1a",
                          borderRadius: "0.375rem",
                          padding: "0.25rem 0.75rem",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Retirer
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {formErrors.files && <p style={errorStyle}>{formErrors.files}</p>}
            </motion.section>
          )}

          {/* ── Signature ── */}
          {form_Data.preference1 &&
            (form_Data.brevetPieceJointe || form_Data.brevetDossier || form_Data.experiencePieceJointe || form_Data.experienceDossier || form_Data.etudesPieceJointe || form_Data.etudesDossier) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div
                  style={{
                    backgroundColor: "#f2f3fe",
                    borderRadius: "0.75rem",
                    padding: "1.5rem",
                    border: formErrors.acknowledgeSignature ? "1.5px solid #ba1a1a" : "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                    <input
                      type="checkbox"
                      id="acknowledgeSignature"
                      checked={acknowledgeSignature}
                      onChange={handleAcknowledgeSignatureChange}
                      style={{ accentColor: "#00447f", width: 18, height: 18, flexShrink: 0, marginTop: 3, cursor: "pointer" }}
                    />
                    <label htmlFor="acknowledgeSignature" style={{ fontSize: "0.875rem", color: "#414751", lineHeight: 1.6, cursor: "pointer" }}>
                      En cochant cette case, je confirme que les informations fournies dans ce formulaire sont exactes et conformes à ma situation. Je fais une demande officielle, laquelle sera transmise aux services des ressources humaines du Centre de services scolaire du Chemin-du-Roy pour traitement, et une copie sera acheminée au Syndicat de l'enseignement des Vieilles-Forges. <strong>Je comprends que le fait de cocher cette case fait office de signature officielle.</strong>
                    </label>
                  </div>
                  {formErrors.acknowledgeSignature && <p style={{ ...errorStyle, marginTop: "0.5rem" }}>{formErrors.acknowledgeSignature}</p>}
                </div>

                {acknowledgeSignature && (
                  <div style={{ marginTop: "1.5rem" }}>
                    <EmailVerification
                      apiUrl={apiUrl}
                      domain={
                        apiUrl?.includes("ngrok")
                          ? ""
                          : apiUrl?.includes("localhost")
                            ? "@gmail.com"
                            : "@csscdr.gouv.qc.ca"
                      }
                      error={formErrors.emailVerification}
                      onVerified={handleEmailVerified}
                    />
                  </div>
                )}
              </motion.div>
            )}

          <button
            type="submit"
            style={{
              backgroundColor: "#00447f",
              color: "#ffffff",
              border: "none",
              borderRadius: "0.5rem",
              padding: "0.875rem 2.5rem",
              fontSize: "1rem",
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "Public Sans, sans-serif",
              alignSelf: "flex-start",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#005ca8")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#00447f")}
          >
            Envoyer
          </button>
          {submitError && (
            <div style={{
              marginTop: "1rem",
              padding: "1rem 1.25rem",
              backgroundColor: "#fff0f0",
              border: "1.5px solid #ba1a1a",
              borderRadius: "0.625rem",
              color: "#ba1a1a",
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}>
              {submitError}
            </div>
          )}
          {loading && (
            <div className="loading-overlay">
              <img src="/loading-logo.gif" alt="Loading..." />
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default DisciplinePreference;
